import Icon from '../../../components/AppIcon';
import LeaveCard from '../../../components/ui/LeaveCard';

const LeaveSummaryPanel = ({
  leaves,
  employeeLeavesStats,
  selectedFilter,
  onFilterChange,
  onCancelLeave,
  onEditLeave,
  onApproveLeave,
  onRejectLeave,
  currentYear,
  isAdmin = false,

  isLoading = false,
  processingId = null,
}) => {
  // Get filtered leaves based on current filter
  const getFilteredLeaves = () => {
    if (selectedFilter === 'All') {
      return leaves;
    }

    // Handle both 'Approved' and 'Accepted' status for backward compatibility
    if (selectedFilter === 'APPROVED' || selectedFilter === 'ACCEPTED') {
      return (
        leaves?.filter((leave) => leave?.status === 'ACCEPTED' || leave?.status === 'APPROVED') ||
        []
      );
    }

    return leaves?.filter((leave) => leave?.status === selectedFilter) || [];
  };

  const filteredLeaves = getFilteredLeaves();

  const filterTabs = [
    {
      key: 'PENDING',
      label: 'Pending',
      count: employeeLeavesStats?.['pending'] || 0,
    },
    {
      key: isAdmin ? 'APPROVED' : 'APPROVED',
      label: isAdmin ? 'Approved' : 'Approved',
      count: employeeLeavesStats?.['approved'] || 0,
    },
    {
      key: 'REJECTED',
      label: 'Rejected',
      count: employeeLeavesStats?.['rejected'] || 0,
    },
    {
      key: 'CANCELLED',
      label: 'Cancelled',
      count: employeeLeavesStats?.['cancelled'] || 0,
    },
    { key: 'All', label: 'All', count: employeeLeavesStats?.['total'] || 0 },
  ];

  return (
    <div className="w-full bg-surface p-4 lg:p-6 overflow-y-auto max-h-[1000px]">
      {/* Header */}
      <div className="mb-4 lg:mb-6">
        <h2 className="text-lg lg:text-xl font-bold text-text-primary mb-2">Leave Management</h2>
        <p className="text-sm text-text-secondary">Track and manage your leave applications</p>
      </div>

      {/* Yearly Stats */}
      <div className="bg-secondary-50 rounded-lg p-3 lg:p-4 mb-4 lg:mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-text-primary">Year {currentYear}</h3>
          <Icon name="Calendar" size={16} color="var(--color-text-secondary)" />
        </div>
        <div className="grid grid-cols-2 gap-2 lg:gap-3">
          <div className="text-center">
            <p className="text-lg font-semibold text-text-primary">{employeeLeavesStats?.total}</p>
            <p className="text-xs text-text-secondary">Total</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold text-yellow-600">{employeeLeavesStats?.pending}</p>
            <p className="text-xs text-text-secondary">Pending</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold text-green-600">{employeeLeavesStats?.approved}</p>
            <p className="text-xs text-text-secondary">{isAdmin ? 'Approved' : 'Accepted'}</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold text-red-600">{employeeLeavesStats?.rejected}</p>
            <p className="text-xs text-text-secondary">Rejected</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold text-red-600">{employeeLeavesStats?.cancelled}</p>
            <p className="text-xs text-text-secondary">Cancelled</p>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mb-4">
        <div className="flex flex-wrap gap-1 bg-secondary-100 p-1 rounded-lg">
          {filterTabs?.map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => onFilterChange?.(key)}
              className={`flex-1 px-2 lg:px-3 py-2 text-xs font-medium rounded-md transition-colors duration-200 ${
                selectedFilter === key
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              <span className="block sm:inline">{label}</span>
              {count > 0 && (
                <span
                  className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                    selectedFilter === key
                      ? 'bg-primary-100 text-primary'
                      : 'bg-secondary-200 text-text-tertiary'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Leave List */}
      <div className="space-y-3 overflow-y-auto">
        {filteredLeaves?.length === 0 ? (
          <div className="text-center py-6 lg:py-8">
            <Icon
              name="FileText"
              size={40}
              className="lg:w-12 lg:h-12"
              color="var(--color-text-tertiary)"
            />
            <p className="text-text-secondary mt-2">No leaves found</p>
            <p className="text-text-tertiary text-sm">
              {selectedFilter === 'All'
                ? "You haven't applied for any leaves yet"
                : `No ${selectedFilter?.toLowerCase()} leaves`}
            </p>
          </div>
        ) : (
          filteredLeaves?.map((leave) => (
            <LeaveCard
              key={leave?.id}
              leave={leave}
              isAdmin={isAdmin}
              onApprove={onApproveLeave ? () => onApproveLeave(leave) : null}
              onReject={onRejectLeave ? () => onRejectLeave(leave) : null}
              onCancel={onCancelLeave ? () => onCancelLeave(leave) : null}
              onEdit={onEditLeave ? () => onEditLeave(leave) : null}
              isLoading={isLoading}
              processingId={processingId}
            />
          ))
        )}
      </div>

      {/* Instructions */}
      <div className="mt-4 lg:mt-6 bg-blue-50 border border-blue-200 rounded-lg p-3 lg:p-4">
        <div className="flex items-start space-x-2 lg:space-x-3">
          <Icon
            name="Info"
            size={14}
            className="lg:w-4 lg:h-4 flex-shrink-0 mt-0.5"
            color="#3B82F6"
          />
          <div className="text-xs text-blue-700">
            <p className="font-medium mb-1">{isAdmin ? 'Admin View:' : 'Tips:'}</p>
            <ul className="space-y-1">
              {isAdmin ? (
                <>
                  <li>• Review and manage employee leave applications</li>
                  <li>• Use filters to view specific leave statuses</li>
                  <li>• Accepted leaves appear on calendar</li>
                </>
              ) : (
                <>
                  <li>• Only pending leaves can be cancelled</li>
                  <li>• Accepted leaves appear on calendar</li>
                  <li>• Apply early for better approval chances</li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaveSummaryPanel;
